export interface LatLng { lat: number; lng: number }
const R = 6371.0088;
const rad = (d: number) => (d * Math.PI) / 180;

/** Great-circle distance in km. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/**
 * Fast flat-earth distance for a search around one centre (within about 50 km it is within 0.2% of haversine).
 * Build it once per search with the centre's latitude, then call it for every candidate.
 */
export function localKm(centre: LatLng): (p: LatLng) => number {
  const ky = (R * Math.PI) / 180, kx = ky * Math.cos(rad(centre.lat)); // same sphere as haversineKm, so both agree
  return (p) => { const dx = (p.lng - centre.lng) * kx, dy = (p.lat - centre.lat) * ky; return Math.sqrt(dx * dx + dy * dy); };
}

/** Cheap pre-filter before haversine. */
export function boundingBox(c: LatLng, km: number) {
  const dLat = (km / R) * (180 / Math.PI);
  const dLng = dLat / Math.max(0.01, Math.cos(rad(c.lat)));
  return { minLat: c.lat - dLat, maxLat: c.lat + dLat, minLng: c.lng - dLng, maxLng: c.lng + dLng };
}
export const inBox = (p: LatLng, b: ReturnType<typeof boundingBox>) =>
  p.lat >= b.minLat && p.lat <= b.maxLat && p.lng >= b.minLng && p.lng <= b.maxLng;

export const validLatLng = (p: { lat: unknown; lng: unknown }): p is LatLng =>
  typeof p.lat === 'number' && typeof p.lng === 'number' && Number.isFinite(p.lat) && Number.isFinite(p.lng) &&
  Math.abs(p.lat) <= 90 && Math.abs(p.lng) <= 180;
